/**
 * Per-tab draft persistence (F8). sessionStorage is scoped to the current
 * tab by design -- a refresh in the same tab restores it, a new tab never
 * sees it, matching "drafts must not be shared between tabs" with no extra
 * work needed.
 *
 * R8: sessionStorage is ~5MB/origin and can throw (quota, private
 * browsing) -- every access is wrapped so a failure here degrades to "no
 * draft persisted" rather than breaking the app.
 */

/** The untitled map as it was right before the first Project was opened over it. */
export interface UntitledStash {
  canonical: string
  /** savedCanonical at stash time, so a restored untitled map keeps its dirty state. */
  baseline: string | null
}

export interface ProjectDraft {
  canonical: string
  currentProjectId: string | null
  untitledStash?: UntitledStash | null
  /** Unsaved changes at write time -- read by the unload guard for every workspace. */
  dirty?: boolean
  /** updated_at / canonical of the stored version the edits are based on (save-conflict check). */
  baseUpdatedAt?: string | null
  baseCanonical?: string | null
}

const DRAFT_PREFIX = 'aetos:project-draft:'

function draftKey(workspaceId: string): string {
  return `${DRAFT_PREFIX}${workspaceId}`
}

/** True when any workspace in this tab has unsaved changes to an opened Project. */
export function hasDirtyDraft(): boolean {
  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i)
      if (!key?.startsWith(DRAFT_PREFIX)) continue
      const draft = JSON.parse(sessionStorage.getItem(key) ?? 'null') as ProjectDraft | null
      if (draft?.currentProjectId && draft.dirty === true) return true
    }
  } catch (error) {
    console.warn('Could not scan project drafts:', error)
  }
  return false
}

export function writeDraft(workspaceId: string, draft: ProjectDraft): void {
  try {
    sessionStorage.setItem(draftKey(workspaceId), JSON.stringify(draft))
  } catch (error) {
    console.warn('Could not persist project draft (storage quota or private mode):', error)
  }
}

export function readDraft(workspaceId: string): ProjectDraft | null {
  try {
    const raw = sessionStorage.getItem(draftKey(workspaceId))
    if (!raw) return null
    return JSON.parse(raw) as ProjectDraft
  } catch (error) {
    console.warn('Could not read project draft:', error)
    return null
  }
}

export function clearDraft(workspaceId: string): void {
  try {
    sessionStorage.removeItem(draftKey(workspaceId))
  } catch (error) {
    console.warn('Could not clear project draft:', error)
  }
}
